import React from 'react'
import { Params } from 'react-router-dom'
import payment_success from 'src/assets/images/icon/icon_check.png'
import payment_fail_or_cancel from 'src/assets/images/icon/icon_message.png'
import CouponPaymentPageView from 'src/pages/CouponPaymentPage/view'
import { QueryClient } from '@tanstack/react-query'
import {
  couponDetailQueryOption,
  couponPurchasedInfoQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { CouponDetailResponseType, CouponPurchasedInfo } from 'src/types/api'
import usePageNav from 'src/hooks/usePageNav.hook'
import { maskPhoneNumber } from 'src/utils'
import { Roboto_num1_b18, Roboto_num1_r18, Roboto_num2_b16 } from 'src/styles/fonts/RobotoFonts'
import { css, RuleSet } from 'styled-components'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import { DateUtils } from 'src/utils/date'

export type ResultContentType = {
  title: string
  desc: string
  img?: { src: string; width: string }
  infoList: {
    text: string
    value: string
    topDivider?: boolean
    bottomDivider?: boolean
    valueFontStyle?: RuleSet<object>
  }[]
  notiText?: {
    default: { title: string; desc: string; align: 'left' | 'center'; color?: string }[]
    hotdeal?: { title: string; desc: string; align: 'left' | 'center'; color?: string }[]
  }
  rePurchaseButton?: { text: string; onClickFn: () => void }
}
export interface CouponPaymentPageProps {
  resultContent: ResultContentType
  couponDetailData?: CouponDetailResponseType | CouponPurchasedInfo.Response
  purchasePageNav: () => void
  mainPageNav: () => void
}

const afterPurchaseStatus = ['purchase_cancel', 'refund_complete']
const afterPaymentStatus = ['payment_success', 'payment_fail', 'payment_cancel']

//paymentStatus params : payment_success, payment_fail, payment_cancel
//purchase_cancel, refund_complete
export const couponPaymentLoader =
  (queryClient: QueryClient) =>
  async ({
    params: { couponType, exception_Idx, paymentStatus },
  }: {
    params: Params<keyof MatchParams>
  }) => {
    if (afterPurchaseStatus.includes(paymentStatus!)) {
      return Promise.allSettled([
        await queryClient.ensureQueryData(
          couponPurchasedInfoQueryOption({ Idx: Number(exception_Idx!) }),
        ),
      ])
    } else {
      return Promise.allSettled([
        await queryClient.ensureQueryData(
          couponDetailQueryOption(couponType!, { CouponIdx: Number(exception_Idx!) }),
        ),
      ])
    }
  }

const CouponPaymentPage = () => {
  const { paymentStatus, couponNumber, couponType, exception_Idx, orderId } = useParamsType()
  const { purchasePageNav, mainPageNav } = usePageNav()
  const { c_tel } = useCustomCookies()
  const { isValidInDays } = DateUtils
  const isAfterPurchaseStatus = afterPurchaseStatus.includes(paymentStatus)
  const isAfterPaymentStatus = afterPaymentStatus.includes(paymentStatus)
  const exceptionIdx = Number(exception_Idx)

  // usePageVisibility((isForeground: boolean) => {
  //   if (isForeground && paymentStatus === 'payment_cancel') {
  //     handleBillgatePaymentRequest(orderId)
  //   }
  // })

  const { data: couponPurchasedQueryData, isLoading: isCouponPurchasedInfo } =
    useCustomQuery<CouponPurchasedInfo.Response>(
      couponPurchasedInfoQueryOption({ Idx: exceptionIdx }, isAfterPurchaseStatus),
    )

  const { data: couponDetailQueryData, isLoading: isCouponDetailLoading } =
    useCustomQuery<CouponDetailResponseType>(
      couponDetailQueryOption(couponType, { CouponIdx: exceptionIdx }, isAfterPaymentStatus),
    )

  const couponDetailInfo = afterPurchaseStatus.includes(paymentStatus)
    ? couponPurchasedQueryData?.Body!
    : couponDetailQueryData?.Body!

  const { SalePrice, RegularPrice, DiscountRate } = couponDetailInfo

  const purchaseDT = couponPurchasedQueryData?.Body?.PurchaseDT ?? ''
  const percentage = purchaseDT ? (isValidInDays(purchaseDT, 365) ? 1 : 0.9) : 1

  const resultContents: {
    [key: string]: ResultContentType
  } = {
    payment_success: {
      title: '결제완료',
      desc: '홈 - 쿠폰지갑에서\n구매한 쿠폰을 확인할 수 있습니다.',
      img: { src: payment_success, width: '35px' },
      infoList: [
        {
          text: '결제금액',
          value: SalePrice?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
        {
          text: '받는사람',
          value: maskPhoneNumber(c_tel),
          valueFontStyle: css`
            ${Roboto_num2_b16};
            color: var(--black-90);
          `,
        },
      ],
      notiText: {
        default: [
          {
            title: '',
            desc: '',
            align: 'left',
          },
          {
            title: '안심하세요',
            desc: '오류로 결제가 정상처리되지 않은 경우, 1~2분 내 자동으로 결제가 취소됩니다.',
            align: 'center',
          },
        ],
      },
    },
    payment_fail: {
      title: '결제실패',
      desc: '결제에 실패했습니다.',
      img: { src: payment_fail_or_cancel, width: '40px' },
      infoList: [
        {
          text: '정가',
          value: RegularPrice?.toLocaleString()!,
          valueFontStyle: css`
            ${Roboto_num1_r18};
            color: var(--black-50);
          `,
        },
        {
          text: '할인율',
          value: `${DiscountRate}%`,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--brand-50);
          `,
        },
        {
          text: '결제금액',
          value: SalePrice?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--black-90);
          `,
        },
      ],
      notiText: {
        default: [
          {
            title: '',
            desc: '',
            align: 'left',
          },
          {
            title: '안심하세요',
            desc: '오류로 결제가 정상처리되지 않은 경우, 1~2분 내 자동으로 결제가 취소됩니다.',
            align: 'center',
          },
        ],
      },
      rePurchaseButton: {
        text: '다시 구매하기',
        onClickFn: () => purchasePageNav(Number(couponType), exceptionIdx, couponNumber),
      },
    },
    payment_cancel: {
      title: '결제취소',
      desc: '결제가 취소되었습니다.',
      img: { src: payment_fail_or_cancel, width: '40px' },
      infoList: [
        {
          text: '정가',
          value: RegularPrice?.toLocaleString()!,
          valueFontStyle: css`
            ${Roboto_num1_r18};
            color: var(--black-50);
          `,
        },
        {
          text: '할인율',
          value: `${DiscountRate}%`,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--brand-50);
          `,
        },
        {
          text: '결제금액',
          value: SalePrice?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--black-90);
          `,
        },
      ],
      rePurchaseButton: {
        text: '다시 구매하기',
        onClickFn: () => purchasePageNav(Number(couponType), exceptionIdx, couponNumber),
      },
    },
    purchase_cancel: {
      title: '구매가 \n취소되었습니다.',
      desc: '카드사 승인/매입 취소에 따라 최종 환불은 1~5일 소요될 수 있습니다.',
      infoList: [
        {
          text: '정가',
          value: RegularPrice?.toLocaleString()!,
          valueFontStyle: css`
            ${Roboto_num1_r18};
            color: var(--black-50);
          `,
        },
        {
          text: '할인율',
          value: `${DiscountRate}%`,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--brand-50);
          `,
        },
        {
          text: '취소금액',
          value: SalePrice?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--black-90);
          `,
        },
      ],
    },
    refund_complete: {
      title: '환불신청이 \n완료되었습니다.',
      desc: '요청하신 환불 처리가 완료될 경우 문자를 발송해 드립니다. 본인 명의의 계좌 확인을 위해 다소 시간이 소요될 수 있습니다.',
      infoList: [
        {
          text: '정가',
          value: RegularPrice?.toLocaleString()!,
          valueFontStyle: css`
            ${Roboto_num1_r18};
            color: var(--black-50);
          `,
        },
        {
          text: '할인율',
          value: `${DiscountRate}%`,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--brand-50);
          `,
        },
        {
          text: '환불예정금액',
          value: (SalePrice * percentage)?.toLocaleString()!,
          topDivider: true,
          valueFontStyle: css`
            ${Roboto_num1_b18};
            color: var(--black-90);
          `,
        },
      ],
    },
  }
  const resultContent = resultContents[paymentStatus]

  const props: CouponPaymentPageProps = {
    couponDetailData: couponDetailInfo!,
    purchasePageNav: () => purchasePageNav(Number(couponType), exceptionIdx, couponNumber),
    resultContent,
    mainPageNav,
  }

  return (
    <React.Fragment>
      <RenderIf when={isCouponDetailLoading || isCouponPurchasedInfo}>
        <Loading.Default />
      </RenderIf>
      <CouponPaymentPageView {...props} />
    </React.Fragment>
  )
}
export default CouponPaymentPage
